import SearchIcon from '@mui/icons-material/Search';
import { Typography } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import type { DashboardT } from 'business/definitions';
import GeneralModal from 'components/SharedComponents/Modals/GeneralModal';
import { useIsExploreUser } from 'hooks/client';
import { useGetDashboardFromSlugOrMetadata } from 'hooks/useGetDashboardFromSlugOrMetadata';
import CreateDashboardButton from 'modules/decision/components/CreateDashboardForm/CreateDashboardButton';
import DashboardList from 'modules/decision/components/DashboardList/DashboardList';
import type { ChangeEvent } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { filterDashboardsByTitle } from 'routes/Library/helpers';
import { useFetchActiveDashboards } from 'shared/api/getDashboardsList';
import AddToDashboardItem from 'shared/components/ChartEditMenu/AddToDashboardItem';
import { useShouldInsertBelow } from 'shared/hooks/useShouldInsertBelow';
import { useSelector } from 'store/hooks';

type Props = {
    show: boolean;
    closeModal: () => void;
    description?: string;
    insertBelowLabel: string;
    onSubmit: (dashboards: DashboardT[], shouldInsertBelow: boolean) => void;
    onCreateDashboard: (dashboard: DashboardT) => void;
};

/** Picks editable dashboards to add to, with the option to insert below on the current one */
const DashboardSelectionModal = ({
    show,
    closeModal,
    description = undefined,
    insertBelowLabel,
    onSubmit,
    onCreateDashboard,
}: Props): JSX.Element => {
    const { t } = useTranslation();
    const { shouldInsertBelow, toggleShouldInsertBelow } =
        useShouldInsertBelow();

    const homeDashboardSlug = useSelector(
        (state) => state.vision.user.homeDashboardSlug,
    );
    const isExploreUser = useIsExploreUser();
    const currentDashboard = useGetDashboardFromSlugOrMetadata();
    const { data: activeDashboards } = useFetchActiveDashboards();
    // Only show dashboard where we have edit right
    const dashboards = activeDashboards
        ? activeDashboards.filter((d) => d.canEdit)
        : [];

    /* Handle dashboards selection */
    const [selection, setSelection] = useState<string[]>([]);
    const submit = () => {
        onSubmit(
            dashboards.filter((dashboard) =>
                selection.includes(dashboard.slug),
            ),
            shouldInsertBelow,
        );
        closeModal();
    };

    /* Handle the selection inside the library. We useCallback to avoid reference issues */
    const changeSelection = useCallback(
        (slug: string) => {
            let newSelection;
            if (selection.includes(slug)) {
                newSelection = selection.filter((s) => s !== slug);
            } else {
                newSelection = [...selection, slug];
            }
            setSelection(newSelection);
        },
        [selection],
    );
    const [filterString, setFilterString] = useState('');
    const filteredDashboards = filterDashboardsByTitle(
        dashboards,
        filterString,
        [],
    );
    const onChangeSearch = (e: ChangeEvent<HTMLInputElement>) =>
        setFilterString(e.target.value);

    const createDashboard = (dashboard: DashboardT) => {
        onCreateDashboard(dashboard);
        closeModal();
    };

    const addToDashboardItem = (dashboard: DashboardT) => (
        <AddToDashboardItem
            key={dashboard.slug}
            dashboard={dashboard}
            isSelected={selection.includes(dashboard.slug)}
            changeSelection={changeSelection}
            isCurrentDashboard={currentDashboard?.key === dashboard.key}
            shouldInsertChartBelow={shouldInsertBelow}
            toggleInsertChartBelow={toggleShouldInsertBelow}
            insertBelowLabel={insertBelowLabel}
        />
    );

    const ModalContent = (
        <>
            {description && (
                <Typography variant="body1">{description}</Typography>
            )}
            <TextField
                value={filterString}
                onChange={onChangeSearch}
                autoFocus
                variant="outlined"
                margin="dense"
                size="small"
                fullWidth
                label={t('dashboards.search')}
                slotProps={{
                    input: {
                        endAdornment: (
                            <InputAdornment position="end">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    },
                }}
            />
            {isExploreUser && (
                <CreateDashboardButton onCreate={createDashboard} />
            )}
            {!!dashboards.length && (
                <DashboardList
                    dashboards={filteredDashboards}
                    homeDashboardSlug={homeDashboardSlug}
                    dashboardItemRender={(dashboard) =>
                        addToDashboardItem(dashboard)
                    }
                />
            )}
        </>
    );

    const closeButton = {
        text: t('common.close'),
        onClick: closeModal,
    };
    const containedButton = {
        text: t('common.add'),
        onClick: submit,
        className: 'add-to-dashboard-confirm',
    };

    return (
        <GeneralModal
            isOpen={show}
            onClose={closeModal}
            showCloseButton={false}
            shouldCloseOnBackdropClick
            containedButton={containedButton}
            textButton={closeButton}
        >
            {ModalContent}
        </GeneralModal>
    );
};

export default DashboardSelectionModal;
